import * as Sentry from "@sentry/react";
import {UserClaims} from "@okta/okta-auth-js";
import {useOktaAuth} from "@okta/okta-react";
import {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {getTenant, Tenant} from "../services/TenantService";
import _ from "lodash";
import log from "loglevel";

type AuthState = {
  user?: UserClaims;
  isAdmin?: boolean;
  isSuperUser?: boolean;
  tenantId?: string;
  tenant?: Tenant;
  groups?: string[];
  tenants?: string[];
  isEcourierUser?: boolean;
  expiresAt?: number;
  isAuthenticated?: boolean;
};

type AuthContextValue = AuthState & {
  setActiveTenant?: (tenantId: string, tenant: Tenant) => void;
};

const AuthContext = createContext<AuthContextValue>({
  user: undefined,
  isAdmin: undefined,
  isSuperUser: undefined,
  tenantId: undefined,
  tenant: undefined,
  groups: undefined,
  tenants: undefined,
  isEcourierUser: undefined,
  expiresAt: undefined,
  isAuthenticated: undefined,
  setActiveTenant: () => {}
});

type AuthProviderProps = {
  children: JSX.Element[] | JSX.Element;
};

// Module-level tenant override. Set by setActiveTenant when the user explicitly
// switches tenants. Survives React component remounts (which reset refs and state)
// but resets on full page reload (module re-evaluation). This prevents the
// getUserAttributes effect from overwriting the tenant with stale JWT claims
// after the Okta SDK triggers a component remount during token renewal.
let explicitTenantOverride: {tenantId: string; tenant: Tenant} | null = null;

const AuthProvider = ({children}: AuthProviderProps) => {
  const {oktaAuth, authState} = useOktaAuth();
  const [state, setState] = useState<AuthState>({
    user: undefined,
    isAdmin: undefined,
    isSuperUser: undefined,
    tenantId: undefined,
    tenant: undefined,
    groups: undefined,
    tenants: undefined,
    isEcourierUser: undefined,
    expiresAt: undefined,
    isAuthenticated: undefined
  });

  const setActiveTenant = useCallback((tenantId: string, tenant: Tenant) => {
    explicitTenantOverride = {tenantId, tenant};
    setState((prev) => ({...prev, tenantId, tenant}));
  }, []);

  useEffect(() => {
    let cancel = false;

    const getUserClaims = async () => {
      log.debug("AuthProvider - Retrieving user claims");
      const authenticatedUser = await oktaAuth.getUser();
      log.debug(`AuthProvider - User claims retrieved: ${JSON.stringify(authenticatedUser)}`);
      if (!cancel) {
        log.debug("AuthProvider - Setting user claims");
        //only update state if it's changed
        if (
          !_.isEqual(authenticatedUser.sub, state.user?.sub) ||
          !_.isEqual(authenticatedUser.email, state.user?.email) ||
          !_.isEqual(authenticatedUser.name, state.user?.name) ||
          !_.isEqual(authenticatedUser.tenantId, state.user?.tenantId) ||
          !_.isEqual(authenticatedUser.legacyUserId, state.user?.legacyUserId)
        ) {
          setState((prevState) => ({
            ...prevState,
            user: authenticatedUser
          }));
        }
      } else {
        log.warn("AuthProvider - User claims process canceled!");
      }
    };

    if (state && authState?.isAuthenticated) {
      log.debug(`AuthProvider - ${new Date()}`);
      getUserClaims();
    } else {
      log.debug("AuthProvider - User token not yet set");
    }

    return () => {
      cancel = true;
    };
  }, [oktaAuth, state, state.user, authState?.isAuthenticated]);

  useEffect(() => {
    let cancel = false;

    const getUserAttributes = async () => {
      if (!cancel) {
        log.debug(`AuthProvider - ${new Date()}`);
        const authenticationToken = oktaAuth.getAccessToken() ?? "";
        const claims = authState?.accessToken?.claims;
        const groupsClaim = claims?.groups as unknown as Array<string>;
        const tenantsClaim = claims?.allowedTenants as unknown as Array<string>;
        const tenantId = authState?.accessToken?.claims?.tenantId as string;
        const isEcourierUser = authState?.accessToken?.claims?.isEcourierUser as boolean;
        const expiresAt = authState?.accessToken?.expiresAt as number;
        const isAuthenticated = authState?.isAuthenticated as boolean;

        //When switching tenants from a different environment, we need to continue so that the tenant selector is still available
        const tenant = tenantId
          ? await getTenant(authenticationToken, tenantId)
              .then((response) => response.data)
              .catch((error) => {
                log.warn(
                  "AuthProvider - Error getting tenant, this is most likely due to switching tenant from a different environment",
                  error
                );
                return undefined;
              })
          : undefined;

        const nonTenantState = {
          isAdmin: groupsClaim?.some((x) => ["dispatch-admins", "dispatch-tenant-admins"].includes(x)) || false,
          isSuperUser: groupsClaim?.includes("dispatch-admins"),
          groups: groupsClaim,
          tenants: tenantsClaim,
          isEcourierUser: isEcourierUser,
          expiresAt: expiresAt,
          isAuthenticated: isAuthenticated
        };

        log.debug(`AuthProvider - Setting user attributes with tenantId: ${tenantId}`);

        setState((prevState) => {
          // If the user explicitly switched tenants, use that tenant
          // regardless of what the JWT claims say. The module-level override
          // survives component remounts that would otherwise let stale JWT
          // claims overwrite the correct tenant.
          const newState: AuthState = explicitTenantOverride
            ? {...nonTenantState, tenantId: explicitTenantOverride.tenantId, tenant: explicitTenantOverride.tenant}
            : {...nonTenantState, tenantId, tenant};

          const changed =
            !_.isEqual(newState.groups, prevState.groups) ||
            !_.isEqual(newState.tenants, prevState.tenants) ||
            !_.isEqual(newState.isEcourierUser, prevState.isEcourierUser) ||
            !_.isEqual(newState.isAuthenticated, prevState.isAuthenticated) ||
            !_.isEqual(newState.tenantId, prevState.tenantId) ||
            !_.isEqual(newState.tenant, prevState.tenant) ||
            !_.isEqual(newState.isAdmin, prevState.isAdmin) ||
            !_.isEqual(newState.isSuperUser, prevState.isSuperUser);

          return changed ? {...prevState, ...newState} : prevState;
        });
      } else {
        log.warn("AuthProvider - User attributes process canceled!");
      }
    };

    if (authState) {
      getUserAttributes();
    }

    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.accessToken, authState?.idToken, authState?.refreshToken]);

  const contextValue = useMemo(() => ({...state, setActiveTenant}), [state, setActiveTenant]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
export type {AuthState};
export {AuthContext};

export const useAuthState = () => useContext(AuthContext);

// Exported for testing — resets module-level tenant override between test cases
export const _resetTenantOverride = () => {
  explicitTenantOverride = null;
};
